import useStateMachine from '@cassiozen/usestatemachine';
import AddLocationAltOutlinedIcon from '@mui/icons-material/AddLocationAltOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import MyLocationOutlinedIcon from '@mui/icons-material/MyLocationOutlined';
import Box from '@mui/material/Box';
import ButtonGroup from '@mui/material/ButtonGroup';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Map from '@planet/maps/Map';
import View from '@planet/maps/View';
import Draw from '@planet/maps/interaction/Draw';
import Modify from '@planet/maps/interaction/Modify';
import MapboxLayer from '@planet/maps/layer/MapboxVector';
import VectorLayer from '@planet/maps/layer/Vector';
import GeoJSON from 'ol/format/GeoJSON';
import {useGeographic as geographic} from 'ol/proj';
import VectorSource from 'ol/source/Vector';
import {object, string} from 'prop-types';
import React, {useCallback, useRef} from 'react';
import {useController, useFormContext} from 'react-hook-form';
import {defaultFeatureStyle, highlightFeatureStyle} from '../../theme.js';
import Geocoder from '../Geocoder.jsx';

geographic();
const format = new GeoJSON();
const accessToken = import.meta.env.VITE_MAPBOX_TOKEN;
const styleUrl = 'mapbox://styles/mapbox/light-v10';

function getCenter(geometry) {
  const geom = format.readGeometry(geometry);
  const extent = geom.getExtent();
  return [(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2];
}

const states = {
  INACTIVE: 'inactive',
  DRAWING: 'drawing',
  DRAWN: 'drawn',
};

const events = {
  START: 'start',
  CANCEL: 'cancel',
  FINISH: 'finish',
};

function GeometryInput({name, rules, defaultValue = null}) {
  const {control} = useFormContext();
  const {field} = useController({name, control, rules, defaultValue});
  const sourceRef = useRef(null);
  const viewRef = useRef(null);

  const onChange = field.onChange;

  const [state, send] = useStateMachine({
    initial: defaultValue ? states.DRAWN : states.INACTIVE,
    states: {
      [states.INACTIVE]: {
        on: {
          [events.START]: states.DRAWING,
        },
        effect() {
          sourceRef.current.clear(true);
          onChange(null);
        },
      },
      [states.DRAWING]: {
        on: {
          [events.CANCEL]: states.INACTIVE,
          [events.FINISH]: states.DRAWN,
        },
      },
      [states.DRAWN]: {
        on: {
          [events.CANCEL]: states.INACTIVE,
          [events.FINISH]: states.DRAWN,
        },
        effect({event}) {
          if (!event.geometry) {
            // initial event
            return;
          }
          onChange(event.geometry);
        },
      },
    },
  });

  const onDrawEnd = useCallback(
    event => {
      const geometry = format.writeGeometryObject(event.feature.getGeometry());
      send({type: events.FINISH, geometry});
    },
    [send],
  );

  const onModifyEnd = useCallback(
    event => {
      const feature = event.features.item(0);
      if (!feature) {
        return;
      }
      const geometry = format.writeGeometryObject(feature.getGeometry());
      send({type: events.FINISH, geometry});
    },
    [send],
  );

  const onZoomToLocationClick = useCallback(() => {
    if (!field.value) {
      return;
    }
    const geom = format.readGeometry(field.value);
    viewRef.current.fit(geom, {duration: 250, maxZoom: 8});
  }, [field.value]);

  if (!sourceRef.current) {
    const source = new VectorSource();
    if (field.value) {
      source.addFeature(format.readFeature(field.value));
    }
    sourceRef.current = source;
  }

  const onDrawClick = useCallback(() => {
    send(events.START);
  }, [send]);

  const onClearClick = useCallback(() => {
    send(events.CANCEL);
  }, [send]);

  const onGeocoderChange = useCallback(
    result => {
      if (!result) {
        return;
      }

      if (result.bbox) {
        viewRef.current.fit(result.bbox, {duration: 250, maxZoom: 10});
      } else {
        viewRef.current.fit(format.readGeometry(result.geometry), {
          duration: 250,
          maxZoom: 8,
        });
      }

      send({type: events.CANCEL});
      send({type: events.START});
      send({type: events.FINISH, geometry: result.geometry});
      sourceRef.current.clear(true);
      sourceRef.current.addFeature(format.readFeature(result.geometry));
    },
    [send],
  );

  const defaultCenter = field.value ? getCenter(field.value) : [0, 0];

  return (
    <Box sx={{height: '100%', position: 'relative'}}>
      <Map>
        <View options={{center: defaultCenter, zoom: 2}} ref={viewRef} />
        <MapboxLayer options={{accessToken, styleUrl}} />
        <VectorLayer
          options={{className: 'render-over-labels'}}
          source={sourceRef.current}
          style={defaultFeatureStyle}
        />
        <Draw
          active={state.value === states.DRAWING}
          options={{
            type: 'Point',
            source: sourceRef.current,
            style: highlightFeatureStyle,
          }}
          onDrawEnd={onDrawEnd}
        />
        <Modify
          active={state.value === states.DRAWN}
          options={{source: sourceRef.current, style: highlightFeatureStyle}}
          onModifyEnd={onModifyEnd}
        />
      </Map>
      <Geocoder
        size="small"
        sx={{position: 'absolute', top: 15, left: 15, width: 250}}
        onChange={onGeocoderChange}
      />
      <ButtonGroup
        sx={{
          position: 'absolute',
          top: 15,
          right: 15,
          backgroundColor: 'background.paper',
        }}
        size="small"
        variant="contained"
        orientation="vertical"
        disableElevation
      >
        <IconButton
          onClick={onDrawClick}
          size="small"
          color={state.value === states.DRAWING ? 'primary' : 'default'}
        >
          <Tooltip title="add location" placement="left">
            <AddLocationAltOutlinedIcon fontSize="inherit" />
          </Tooltip>
        </IconButton>
        <IconButton size="small" onClick={onZoomToLocationClick}>
          <Tooltip title="center on location" placement="left">
            <MyLocationOutlinedIcon fontSize="inherit" />
          </Tooltip>
        </IconButton>
        <IconButton onClick={onClearClick} size="small">
          <Tooltip
            placement="left"
            title={
              state.value === states.DRAWING ? 'cancel' : 'remove location'
            }
          >
            <DeleteOutlinedIcon fontSize="inherit" />
          </Tooltip>
        </IconButton>
      </ButtonGroup>
    </Box>
  );
}

GeometryInput.propTypes = {
  name: string.isRequired,
  rules: object,
  defaultValue: object,
};

export default GeometryInput;
